import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import EmailOtpForm from '../components/auth/EmailOtpForm';
import { AuthContext } from '../context/AuthContext';
import { AUTH_CONFIG } from '../config/constants';
import { validateToken } from '../utils/authUtils';
import Alert from '../components/ui/Alert';
// Import hero image
import heroImage from '../assets/hero-image-1.png';

// Custom CSS for responsive layout
const styles = {
    mobileSection: {
        minHeight: '250px'},
    desktopSection: {
        height: '100%'},
    responsiveForm: {
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        backgroundColor: '#ffffff',
        borderRadius: '8px'},
    leftColumn: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center'},
    contentContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto'},
    rightColumn: {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'},
    authContainer: {
        minHeight: '100vh',
        background: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'}
};

const EmployerAuthPage = () => {
    const { setAuthenticatedUser, isAuthenticated, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    
    // UI state
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    
    // Capture referral code from URL on page load
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const refCode = params.get('ref');
        
        if (refCode) {
            // Store referral code in localStorage for later use during profile completion
            localStorage.setItem('pendingReferralCode', refCode.toUpperCase());
            console.log('📌 Captured referral code:', refCode.toUpperCase());
        }
    }, [location.search]);
    
    // Check if mobile view
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 992);
        };
        
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        
        return () => {
            window.removeEventListener('resize', checkIfMobile);
        };
    }, []);
    
    // Helper function to wait for state propagation and verify data integrity
    const waitAndVerifyAuth = async () => {
        // Give React time to process state updates from setAuthenticatedUser
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Verify localStorage has the required data
        const storedToken = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
        const storedUser = localStorage.getItem(AUTH_CONFIG.USER_KEY);
        
        if (!storedToken || !storedUser) {
            console.error('[ERROR] localStorage check failed');
            return { success: false, error: 'storage_missing' };
        }

        // Give a bit more time for the AuthContext state to settle
        // The ProtectedRoute will handle final validation with its grace period
        await new Promise(resolve => setTimeout(resolve, 100));
        
        return { success: true };
    };
    
    // Handle successful authentication
    const handleAuthSuccess = async (response) => {

        setIsAuthenticating(true);
        
        try {
            // Extract data from the response (backend wraps it in a 'data' property)
            const data = response.data || response;

            // Validate response structure
            if (!data || !data.user || !data.token) {
                console.error('Invalid authentication response structure:', data);
                setError('Invalid authentication response from server');
                setIsAuthenticating(false);
                return;
            }
            
            // Validate required user fields
            if (!data.user.id || !data.user.userType || !data.user.email) {
                console.error('Missing required user fields:', data.user);
                setError('Invalid authentication response from server');
                setIsAuthenticating(false);
                return;
            }
            
            // Validate userType for employers
            if (data.user.userType !== 'employer') {
                console.error('UserType mismatch: expected employer, got', data.user.userType);
                setError('This login page is for employers only. Please use the correct login page.');
                setIsAuthenticating(false);
                return;
            }
            
            // Validate token format
            const tokenValidation = validateToken(data.token);
            if (!tokenValidation.valid) {
                console.error('Invalid token format:', tokenValidation.error);
                setError('Received invalid authentication token');
                setIsAuthenticating(false);
                return;
            }
            
            // Store token and user data with timestamp
            localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, data.token);
            localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(data.user));
            localStorage.setItem('authTokenCreatedAt', Date.now().toString());
            
            // Update auth context - verify the result

            let authResult;
            try {
                authResult = await setAuthenticatedUser({ 
                    token: data.token, 
                    user: data.user 
                });

                // Check if setAuthenticatedUser returned false (failure)
                if (authResult === false) {
                    throw new Error('Failed to set authenticated user');
                }
            } catch (authErr) {
                console.error('[ERROR] setAuthenticatedUser failed:', authErr);
                // Clear any stored auth data
                localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
                localStorage.removeItem(AUTH_CONFIG.USER_KEY);
                setError('Failed to authenticate. Please try again.');
                setIsAuthenticating(false);
                return;
            }
            
            // Show success message with user email only after successful authentication
            setSuccess(`Welcome back, ${data.user.email}!`);

            // Wait for state to settle and verify localStorage
            const verification = await waitAndVerifyAuth();
            
            if (!verification.success) {
                console.error('[ERROR] Auth verification failed:', verification.error);
                setError('Authentication failed - please try again');
                setIsAuthenticating(false);
                return;
            }
            
            // Log token verification for debugging
            const finalToken = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
            const finalUser = localStorage.getItem(AUTH_CONFIG.USER_KEY);
            console.log('[DEBUG] Final local storage state:', {
                hasToken: !!finalToken,
                hasUser: !!finalUser,
                userType: finalUser ? JSON.parse(finalUser).userType : null
            });

            // Navigate to the appropriate page based on profile completion
            const targetPath = data.user.profileCompleted ? '/employer/dashboard' : '/employer/complete-profile';

            // Navigate - ProtectedRoute's grace period will handle final auth verification
            navigate(targetPath, { replace: true });
            
        } catch (err) {
            console.error('Error in handleAuthSuccess:', err);
            // Clear localStorage on error
            localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
            localStorage.removeItem(AUTH_CONFIG.USER_KEY);
            setError('Authentication failed. Please try again.');
            setIsAuthenticating(false);
        }
    };
    
    return (
        <Container fluid className="p-0" style={styles.authContainer}>
            <div className="h-100 d-flex align-items-center justify-content-center">
                <div style={{
                    ...styles.contentContainer,
                    flexDirection: isMobile ? 'column' : 'row'
                }}>
                    {/* Left Column - Hero Image (hidden on mobile) */}
                    <div className="d-none d-lg-flex" style={styles.leftColumn}>
                        <img 
                            src={heroImage} 
                            alt="Recruitment illustration" 
                            className="img-fluid" 
                            style={{ width: '400px', height: 'auto' }} 
                        />
                    </div>
                    
                    {/* Right side - Authentication form */}
                    <div className={`${isMobile ? 'w-100' : ''}`} style={{
                        ...styles.rightColumn,
                        paddingLeft: isMobile ? '0' : '40px',
                        width: isMobile ? '100%' : 'auto',
                        marginTop: isMobile ? '20px' : '0'
                    }}>
                        <div 
                            className="p-8 mx-auto" 
                            style={{ 
                                ...styles.responsiveForm, 
                                marginTop: isMobile ? '1rem' : '0', 
                                background: '#ffffff',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                                padding: '2.5rem'
                            }}
                        >
                            <h2 className="text-2xl font-bold mb-6" style={{ fontSize: '32px', color: '#000' }}>
                                Employer Login
                            </h2>
                            
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key="email-auth"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <EmailOtpForm 
                                        onSuccess={handleAuthSuccess}
                                        userType="employer"
                                        buttonText="Continue"
                                        disabled={isAuthenticating}
                                    />
                                </motion.div>
                            </AnimatePresence>

                            {/* Error and success messages */}
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-4"
                                >
                                    <Alert variant="danger">
                                        {error}
                                    </Alert>
                                </motion.div>
                            )}

                            {success && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-4"
                                >
                                    <Alert variant="success">
                                        {success}
                                    </Alert>
                                </motion.div>
                            )}
                            
                            {/* Loading indicator during authentication */}
                            {isAuthenticating && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="mt-3 text-center"
                                    style={{ color: '#666', fontSize: '14px' }}
                                >
                                    Redirecting...
                                </motion.div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
};

export default EmployerAuthPage;
